import { SelectInput } from './select.mjs';
import OAuth from '../controls/oauth.mjs';
import { Picker } from '../controls/picker.mjs';
import EVENTS from '../events.mjs';
import {
	composedInputBuilder,
	create,
	registerCustomElement,
	resolveDefaultInputValue,
	createAddFirstAccountBtn,
	getGroupedStoreItems,
} from '../utils.mjs';
import { I18n } from '../helpers/i18n.mjs';
import { DEFAULT_THEME } from '../helpers/color.mjs';
import ConnectionManager from '../controls/connectionManager.mjs';
import RemoteForm from '../controls/remoteFormPanel.mjs';
import './account.scss';
import FormanConfig from '../formanConfig.mjs';
import EventManager from '../eventManager.mjs';
import { getAdjustedColor, isVisibleOnWhite } from './utils/get-adjusted-color.mjs';

const config = FormanConfig.config.fields.account;

const aiAccountsIcons = {
	default: { name: 'ai-provider', theme: '#7302d5' },
	'ai-agent': { name: 'ai-agent', theme: '#7302d5' },
	'ai-local-agent': { name: 'ai-local-agent', theme: '#7302d5' },
	'openai-gpt-3': { name: 'openai-gpt-3', theme: '#10a37f' },
	'anthropic-claude': { name: 'anthropic-claude', theme: '#cc9b7a' },
	'mistral-ai': { name: 'mistral-ai', theme: '#ff6f00' },
	cohere: { name: 'cohere', theme: '#39594d' },
	groq: { name: 'groq', theme: '#f55036' },
	'ai-agent-xai': { name: 'xai', theme: '#000000' },
	'gemini-ai-q9zyjp': { name: 'gemini-ai', theme: '#4f8df5' },
	'gemini-ai': { name: 'gemini-ai', theme: '#4f8df5' },
};

const AI_AGENT_PACKAGES = new Set(['ai-agent', 'ai-local-agent']);

export class AccountInput extends SelectInput {
	constructor() {
		super();

		this.panel = null;
		this._firstCreationMode = false;
	}

	get allowApiCall() {
		return true;
	}

	get hasModeSwitch() {
		return this.mappable;
	}

	get stateDataProps() {
		return ['scoped', 'connection'];
	}

	get selectedAccount() {
		return this._getAccountById(this.value);
	}

	afterNestedRendered() {
		this._checkScenarioInputValidity();
	}

	extendInstructions(instructions, value, metadata) {
		instructions.options = instructions.options || {};
		instructions.options.label = instructions.options.label || config.api.list.label;
		instructions.options.value = instructions.options.value || config.api.list.value;
		instructions.options.wrapper = instructions.options.wrapper || config.api.list.wrapper;
		instructions.options.placeholder = instructions.options.placeholder || 'Choose an account';
		instructions.options.default = instructions.required ? 'scoped' : null;
		instructions.options.team = this.getTeamId(instructions);
		if (!instructions.options.team) instructions.grouped = true;
		instructions.options.organization = this.form.meta.organizationId;
		instructions.connectionTypes = (this.getAttribute('type') || '').split(',').map((type) => {
			let scope = instructions.options.scope || this.form.meta.module.config?.scope || instructions.scope;

			if (scope && !Array.isArray(scope)) scope = scope[type];

			return {
				type,
				scope: scope || [],
			};
		});

		if (this.form.meta.module.package && !instructions.help) {
			if (this.form.meta.module.package.docs) {
				instructions.help = I18n.l(
					'hints.accountCustom',
					{
						service: this.form.meta.module.package.label,
						docs: this.form.meta.module.package.docs,
					},
					true,
				);
			} else {
				instructions.help = I18n.l(
					'hints.account',
					{
						service: this.form.meta.module.package.label,
						name: this.form.meta.module.package.name.replace(/^app#/, ''),
					},
					true,
				);
			}
		}

		const storeQuery = new URLSearchParams();

		if (this.form?.meta?.personalConnections) {
			storeQuery.append('personalConnections', true);
		}

		if (instructions.scenarioInput) {
			storeQuery.append('scenarioInputs', true);
		}

		storeQuery.append('teamId', instructions.options.team);
		storeQuery.append('pg[limit]', config.api.list.limit);
		instructions.connectionTypes.forEach((acc) => {
			storeQuery.append('type', acc.type);
			acc.scope.forEach((scope) => storeQuery.append(acc.type, scope));
		});

		instructions.options.store = `${
			this.form?.options?.personalConnections || (instructions.accountInputPanel && instructions.personalConnections)
				? config.api.list.pdcPath
				: config.api.list.path
		}?${storeQuery}`;
		instructions.theme = instructions.theme || this.form.meta.module?.package?.theme || DEFAULT_THEME;

		return instructions;
	}

	_getAccountById(value) {
		let grupped = false;
		let store = [];

		if (this._originalStore?.length) {
			store = this._originalStore;
		} else {
			store = this.options?.store;
			const picker = this.querySelector('imt-picker');

			grupped = picker.hasAttribute('grouped');
		}

		if (store && value) {
			const findAcc = (arr) => {
				if (!arr) {
					return null;
				}

				return arr.find((x) => x[this._instructions.options.value || config.api.list.value] === value);
			};

			if (grupped) {
				let acc = null;

				store.some((x) => {
					acc = findAcc(x.options);

					if (acc) {
						return true;
					}

					return false;
				});

				return acc;
			} else {
				return findAcc(store);
			}
		}

		return null;
	}

	_shouldLoadOnBuild(options, value) {
		return typeof value !== 'undefined' && value !== '';
	}

	_getRealAccountId(id) {
		if (id?.startsWith && id.startsWith('TAC_')) {
			return +id.replace('TAC_', '');
		}

		return id;
	}

	_checkScenarioInputValidity() {
		if (this.form.options.personalConnections && this.form.meta.module.id) {
			const invalid = !!this._control?.getPickerDisplay()?.querySelector('.scenario-input.no-value');

			if (invalid) {
				this.nestedFieldset?.disable();
			} else {
				this.nestedFieldset?.enable();
			}

			this._runValidators(false);
		}
	}

	/**
	 * Builds the input dom.
	 *
	 * @param {object} instructions Collection of directives.
	 * @param {any} value Value.
	 * @param {object} metadata Metadata.
	 */

	async _build(instructions, value, metadata) {
		// Picker
		const picker = create('imt-picker');

		if (typeof value === 'undefined') {
			value = resolveDefaultInputValue(this, instructions.type);
		}

		// SCENARIO INPUTS
		const { imlOptions } = this.form?.meta;
		const enableAccountScenarioInput = this.form.options.dynamicConnections; // this.form.options.personalConnections;
		const isModuleConfigPanel = !!this.form.meta.module.id;
		const isScenarioInputPanel = instructions.accountInputPanel;

		// run once panel - not very safe, temporary fix for celonis, should be improved
		if (!isModuleConfigPanel && !isScenarioInputPanel && this.form?.options?.personalConnections && this.form.meta) {
			this.form.meta.personalConnections = true;
		}

		picker.register(Picker.COMPONENTS.OPTION_RENDERER, (option, item) => {
			if (item.metadata?.connectionLabel) {
				option.textContent += ` (${item.metadata.connectionLabel})`;
			} else if (item.metadata?.value) {
				option.textContent += ` (${item.metadata.value})`;
			}
			option.setAttribute('data-scoped', item.scoped);
			option.setAttribute('data-connection', item.accountName);

			if (enableAccountScenarioInput && item?.scenarioInput) {
				option.setAttribute('data-scenario-input', true);
				option.setAttribute('data-name', item.name);
				const scenarioInputWrapper = create('div.scenario-input-wrapper.d-flex.justify-content-between');
				const div = create('div.scenario-input ' + item.name);

				const noValueOption = () => {
					option.setAttribute('data-scenario-input-no-value', true);
					div.classList.add('no-value');
					option.textContent = I18n.l('personalConnections.noValueAssigned');
				};

				if (imlOptions?.buildTimeVariables?.[item.name]) {
					const accId = this._getRealAccountId(imlOptions.buildTimeVariables[item.name]);
					const btv = this._getAccountById(accId);

					if (!btv && !item.connection) {
						noValueOption();
					} else {
						option.setAttribute('data-scoped', btv?.scoped);
						option.setAttribute('data-connection', btv?.accountName || item.connection);
						option.textContent = btv?.name || item.connection;
					}
				} else {
					noValueOption();
				}

				const editBtn = create('button.btn.btn-xs.btn-outline-secondary.scenario-input-edit Edit');

				editBtn.addEventListener('click', async (event) => {
					// event.stopPropagation();
					// this._useScenarioInput(instructions, editBtn, btvEditData)(event);

					const name = editBtn.closest('imt-option').getAttribute('data-name');
					const value = this._getRealAccountId(this.form?.meta?.imlOptions?.buildTimeVariables[name]);

					this._useScenarioInput(instructions, picker, { name, value })(event);
				});

				option.insertBefore(scenarioInputWrapper, option.firstChild);
				scenarioInputWrapper.append(div);
				scenarioInputWrapper.append(editBtn);
			}

			// Add icons for ai agent accounts
			// TODO: find better way to get app data
			const packageName = this?.form?.meta?.module?.package?.name;
			if (option?.childNodes?.length && AI_AGENT_PACKAGES.has(packageName) && item?.accountName) {
				const appData = aiAccountsIcons[item.accountName] || aiAccountsIcons[packageName] || aiAccountsIcons.default;
				const icon = create(`img.account-icon[src="static/img/packages/${appData.name}_32.png"]`);
				icon.style.backgroundColor = appData.theme;

				option.insertBefore(icon, option.firstChild);
			}
		});

		if (enableAccountScenarioInput || isScenarioInputPanel) {
			picker.register(Picker.COMPONENTS.DISPLAY_CHANGED, (elm, value) => {
				const selected = picker.querySelector('imt-option[selected]');

				// TODO: add util fn
				if (selected?.getAttribute('data-scenario-input') === 'true') {
					elm.innerHTML = '';
					const div = create('div.scenario-input ' + selected?.getAttribute('data-name'));

					if (selected?.getAttribute('data-scenario-input-no-value') === 'true') {
						div.classList.add('no-value');
					}

					elm.append(div);
				}
			});

			picker.register(Picker.COMPONENTS.OPTIONS_NORMALIZED, (options) => {
				const scenarioIo =
					Array.isArray(imlOptions?.variables) &&
					imlOptions.variables.find((x) => x.category === 'scenario_inputs')?.items;
				// account:github,github2 => [github, github2]
				const accountTypes = this.inputType?.split(':')[1]?.split(',') || [];
				const accountInputs =
					scenarioIo &&
					Object.values(scenarioIo).filter(
						(x) => x.type && x.type.startsWith('account') && accountTypes.includes(x.type.split(':')[1]),
					);

				if (Array.isArray(options?.store) && accountInputs) {
					accountInputs.forEach((x) => {
						let name = x?.name;

						if (name?.match) {
							const pattern = /{{var\.input\.(\w+)}}/;
							const match = name.match(pattern);

							if (match) {
								name = match[1];
							}
						}

						options.store.push({
							id: x.name,
							name,
							scenarioInput: true,
						});
					});
				}

				const groupsConfig = [
					{
						label: I18n.l('accounts.teamConnections'),
						filter: (x) => 'teamId' in x && !x.scenarioInput,
						defaultGroup: true,
					},
				];

				const schedulingType = this.form.meta?.inspector?.blueprint?.scheduling?.type;
				if ((!isModuleConfigPanel && schedulingType === 'on-demand') || isScenarioInputPanel) {
					groupsConfig.push({
						label: I18n.l('accounts.personalConnections'),
						filter: (x) => !('teamId' in x) && !x.scenarioInput,
					});
				}
				if (isModuleConfigPanel && !isScenarioInputPanel) {
					groupsConfig.push({ label: I18n.l('accounts.dynamicConnections'), filter: (x) => x.scenarioInput });
				}
				this._originalStore = options?.store?.slice ? options.store.slice() : [];
				options.store = getGroupedStoreItems(options.store, groupsConfig);
				picker.setAttribute('grouped', '');

				return options;
			});

			this.addEventListener(EVENTS.ACCOUNT.SCENARIO_INPUT_CREATED, () => {
				this.querySelector('.create-connection')?.remove();
				this._firstCreationMode = false;
			});

			this.addEventListener(EVENTS.ACCOUNT.SCENARIO_INPUT_EDITED, async (event) => {
				const { account, inputName, originalInputName } = event.detail;

				if (inputName !== originalInputName) {
					delete this.form.meta.imlOptions.buildTimeVariables[originalInputName];
				}

				this.form.meta.imlOptions.buildTimeVariables[inputName] = !isNaN(+account) ? 'TAC_' + account : account;

				const editedOption = picker.querySelector(`imt-option[data-name="${originalInputName}"]`);
				const acc = this._getAccountById(account);
				const value = `{{var.input.${inputName}}}`;

				picker.querySelector('.display .scenario-input').innerText = inputName;
				editedOption.setAttribute('data-name', inputName);
				editedOption.querySelector('.scenario-input').innerText = inputName;
				editedOption.value = value;

				editedOption.childNodes[1].textContent = acc?.name;

				this.querySelector('imt-nested').remove();
				this._control.value = value;

				const displayInput = picker.querySelector('.display > .scenario-input.no-value');

				if (displayInput) {
					displayInput.classList?.remove('no-value');
					editedOption?.querySelector('.scenario-input.no-value')?.classList?.remove('no-value');
				}
			});
		}

		// Extend of the connection
		picker.addEventListener(EVENTS.PICKER.CHANGE, this._extendHandler(instructions));

		// Do not select default option if the connection doesn't have sufficient scope
		picker.addEventListener(EVENTS.PICKER.BEFORE_SELECT_OPTION, (event) => {
			const option = event.detail.option;

			if (option && option.getAttribute('data-scoped') === 'false') {
				event.preventDefault();
			}
		});

		// Build the component
		metadata.build = Object.assign(metadata.build || {}, { picker });

		if (enableAccountScenarioInput && this.form.meta.module.id) {
			this.registerCustomValidator(() => {
				// check if current item is no-value
				const invalid = Array.from(this.querySelectorAll(`imt-option[data-scenario-input-no-value=true]`) || []).some(
					(x) => x?.value === this.value,
				);

				if (invalid) {
					this.untouched = false;

					return {
						message: I18n.l('accounts.builtTimeValueMissing'),
						valid: false,
					};
				}

				return {
					valid: true,
				};
			});
		}

		await super._build(instructions, value, metadata);

		const buttons = this.showAddBtn
			? [
					{
						label: I18n.l('common.add'),
						action: this._createHandler(instructions),
					},
			  ]
			: [];

		if (enableAccountScenarioInput && this.form.meta.module.id && !instructions.accountInputPanel) {
			buttons.push({
				label: I18n.l('personalConnections.useAsScenarioInput'),
				collapsible: true,
				action: this._useScenarioInput(instructions, picker),
			});
		}

		const findPackage = (accType) => {
			const usedPackages = this.form.meta?.repository?.packages;

			if (usedPackages && accType && accType.indexOf(':') > -1) {
				let app;

				Object.values(usedPackages).forEach((a) => {
					Object.values(a).forEach((appVersion) => {
						if (
							appVersion &&
							appVersion?.dependencies?.accounts?.includes &&
							accType
								.split(':')[1]
								.split(',')
								.some((acc) => appVersion.dependencies.accounts.includes(acc))
						) {
							app = appVersion;
						}
					});
				});

				return app;
			}

			return null;
		};

		const appData = {
			name: this.form.meta.module?.package?.name,
			theme: instructions.theme,
		};

		if (!appData.name) {
			const pkg = findPackage(instructions.type);

			if (pkg) {
				appData.name = pkg.name;
				appData.theme = pkg.theme?.toHex();
			}
		}

		// Build composed input
		this.append(
			composedInputBuilder({
				picker,
				config,
				name: appData.name || instructions.name,
				theme: appData.theme,
				buttons,
				readonly: this.readonly,
				image: instructions?.image,
			}),
		);

		this._firstCreationMode = !this.hasStoreItems && this._control._loaded;

		picker.addEventListener(EVENTS.PICKER.LOAD_DONE, () => {
			this._firstCreationMode = !this.hasStoreItems;
		});

		picker.addEventListener(EVENTS.PICKER.LOAD_ERROR, () => {
			this._firstCreationMode = false;
		});

		// Show connection creation btn if options list is empty
		if (this._firstCreationMode) {
			// Create button for account creation

			picker.before(
				createAddFirstAccountBtn(
					appData.theme,
					I18n.l('accounts.createConnection'),
					this._createHandler(instructions),
					I18n.l('panels.account.creating'),
				),
			);
		}

		this.setThemeVariables(appData);
		this.setIconClickHandler();

		this._checkScenarioInputValidity();
	}

	_createHandler(instructions) {
		return async (event) => {
			if (this.panel) return;

			this.disabled = true;

			if (config?.api?.instructions?.redirect) {
				window.location.href = config.api.instructions.redirect;
				return;
			}

			this.panel = (
				await ConnectionManager.getCreateConnectionPanel(
					instructions,
					event.target,
					!!this.form?.meta?.personalConnections,
					{},
					this.form,
				)
			).handler;

			this.panel.addEventListener(EVENTS.FORM.SUBMITTED, async (event) => {
				this.disabled = false;
				try {
					const connection = event.detail.data;
					const connectionId = connection[0];
					let optionGrupLabel = undefined;
					let defaultGroup = false;

					if (
						(this.form?.options?.personalConnections || instructions.accountInputPanel) &&
						this.querySelector('imt-picker')?.hasAttribute('grouped')
					) {
						if (connectionId && connectionId.startsWith && connectionId.startsWith('PAC_')) {
							optionGrupLabel = I18n.l('accounts.personalConnections');
						} else {
							optionGrupLabel = I18n.l('accounts.teamConnections');
							defaultGroup = true;
						}
					}

					const connectionData = {
						id: connectionId,
						name: connection[1],
						connection: connection[2],
						accountName: connection[2],
						metadata: connection[3],
						scoped: true,
					};

					EventManager.dispatch(this, EVENTS.ACCOUNT.ACCOUNT_CREATED, {
						detail: connectionData,
						cancelable: true,
						bubbles: true,
					});

					await this.addOption(connectionData, optionGrupLabel, defaultGroup);

					if (this._firstCreationMode) {
						this.querySelector('.create-connection')?.remove();
						this._firstCreationMode = false;

						const wrapper = this.querySelector('.input-group');

						if (wrapper) {
							wrapper.style.justifyContent = 'auto';
						}
					}
					// eslint-disable-next-line no-unused-vars
				} catch (ex) {
					throw new Error(I18n.l('accounts.failedAdd'));
				}
			});

			this.panel.addEventListener(EVENTS.PANEL.CLOSE, (event) => {
				this.disabled = false;
				this.panel = null;

				if (this._firstCreationMode) {
					const connectionCreationBtn = this.querySelector('.create-connection');

					if (connectionCreationBtn) {
						connectionCreationBtn.classList.remove('in-progress');
						connectionCreationBtn.textContent = I18n.l('accounts.createConnection');
					}
				}
			});

			this.panel.open();
		};
	}

	_useScenarioInput(instructions, attachToSibling, editData) {
		return async (event) => {
			this.panel = (
				await this._getScenarioInputPanel(
					instructions,
					attachToSibling?.nextSibling?.querySelector('button') || attachToSibling,
					editData,
				)
			).handler;

			this.panel.addEventListener(EVENTS.FORM.SUBMITTED, async (event) => {
				this.disabled = false;
				try {
					const result = event.detail.value;
					const variable = `{{var.input.${result.inputName}}}`;
					const isEdit = !!editData;

					result.inputType = result.connection ? `account:${result.connection}` : instructions.type;
					result.connectionTypes = result.connection
						? instructions.connectionTypes?.filter((x) => x.type === result.connection)
						: instructions.connectionTypes;

					if (!this.form.meta.imlOptions.buildTimeVariables) {
						this.form.meta.imlOptions.buildTimeVariables = {};
					}

					if (!isEdit) {
						this.form.meta.imlOptions.buildTimeVariables[result.inputName] = result.account;

						await this.addOption(
							{
								id: variable,
								name: result.inputName,
								connection: result.label,
								scenarioInput: true,
								scoped: result.scoped,
							},
							I18n.l('accounts.dynamicConnections'),
						);

						this.value = variable;

						EventManager.dispatch(this, EVENTS.ACCOUNT.SCENARIO_INPUT_CREATED, {
							bubbles: true,
							detail: result,
						});
					} else {
						result.originalInputName = editData.name;

						EventManager.dispatch(this, EVENTS.ACCOUNT.SCENARIO_INPUT_EDITED, {
							bubbles: true,
							detail: result,
						});
					}
					// eslint-disable-next-line no-unused-vars
				} catch (ex) {
					throw new Error(I18n.l('accounts.failedAdd'));
				}
			});

			this.panel.addEventListener(EVENTS.PANEL.CLOSE, (event) => {
				this.disabled = false;
				this.panel = null;

				if (this._firstCreationMode) {
					const connectionCreationBtn = this.querySelector('.create-connection');

					if (connectionCreationBtn) {
						connectionCreationBtn.classList.remove('in-progress');
						connectionCreationBtn.textContent = I18n.l('accounts.createConnection');
					}
				}
			});

			this.panel.open();
		};
	}

	async _getScenarioInputPanel(instructions, target, editData) {
		const panel = new RemoteForm(target);

		const pascalCaseName = (name) => {
			if (!name) {
				return name;
			}
			let result = '';

			name.split(' ').forEach((x) => {
				result += x.charAt(0).toUpperCase() + x.slice(1);
			});

			return result;
		};

		const isOnDemand = this.form.meta?.inspector?.blueprint?.scheduling?.type === 'on-demand';

		const connectionInstructions = {
			...instructions,
			...{
				name: 'account',
				required: true,
				label: I18n.l('accounts.bulidTimeValue'),
				help: I18n.l('accounts.buildTimeValueHelp'),
				theme: instructions.theme.toHex ? instructions.theme.toHex() : instructions.theme,
				accountInputPanel: true,
				personalConnections: this.form?.options?.personalConnections,
				default: editData ? editData.value : undefined,
				nested: [
					{
						name: 'asDefault',
						type: 'boolean',
						label: 'Use as default value',
						help: I18n.l('accounts.buildTimeAsDefaultHint'),
						default: true,
						reversedNested: true,
						required: true,
						nested: [
							{
								name: 'defaultAccount',
								type: instructions.type,
								required: !isOnDemand,
								label: 'Default value',
								help: I18n.l('accounts.defaultValueHint'),
								theme: instructions.theme.toHex ? instructions.theme.toHex() : instructions.theme,
								personalConnections: this.form?.options?.personalConnections,
							},
						],
					},
				],
			},
		};

		await panel.build(
			{
				name: I18n.l(!editData ? 'accounts.createInput' : 'accounts.updateInput'),
				help: (target && target.closest('imt-panel')?.help) || this.form?.help || config.kb.path,
				config: [
					{
						label: I18n.l('accounts.scenarioInput'),
						name: 'inputName',
						required: true,
						default: editData
							? editData.name
							: I18n.l('{{!accounts.scenarioInputDefault}}', {
									service: pascalCaseName(this.form?.module?.package?.label),
							  }),
						validate: {
							pattern: {
								regexp: '^_*[a-zA-Z][0-9a-zA-Z_]*$',
								label: I18n.l('accounts.scenarioInputPatternHelp'),
							},
						},
					},
					connectionInstructions,
				],
				buttons: [
					{
						label: '{{!common.close}}',
						action: 'close',
					},
					{
						label: '{{!common.save}}',
						action: 'handler',
						handler: (e) => {
							if (!panel.form.validate()) return;

							const accOption = panel.form.querySelector(
								`imt-input-account imt-picker imt-option[value='${JSON.stringify(panel.form.value.account)}']`,
							);
							const value = {
								...panel.form.value,
								...{
									connection: accOption?.getAttribute('data-connection'),
									scoped: accOption?.getAttribute('data-scoped'),
									label: accOption?.textContent,
								},
							};

							if (
								EventManager.dispatch(panel, EVENTS.FORM.SUBMITTED, {
									detail: {
										value,
										panel,
										form: panel.form,
									},
									cancelable: true,
								})
							) {
								panel.panel.close();
							}
						},
					},
				],
			},
			{ personalConnections: this.form?.options?.personalConnections },
		);

		const forceDefaultValue = () => {
			const f = panel.panel.querySelector('imt-forman');
			const asDefault = f.querySelector('[name="asDefault"]');
			const { account } = f.value;

			if (asDefault) {
				if (account?.startsWith && account.startsWith('PAC_')) {
					asDefault.value = false;
					asDefault.disabled = true;

					setTimeout(() => {
						f.querySelector('[name="defaultAccount"]').disabled = false;
					}, 0);
				} else {
					asDefault.disabled = false;
				}
			}
		};

		panel.panel.addEventListener('input', () => {
			forceDefaultValue();
		});
		forceDefaultValue();

		if (!editData) {
			const inputName = panel.form.querySelector('imt-input-text[name=inputName]');

			inputName.registerCustomValidator((value) => {
				const btvs = panel?.form?.meta?.imlOptions?.buildTimeVariables;

				if (btvs && btvs[value]) {
					return {
						valid: false,
						message: I18n.l('accounts.scenarioInputNameInUse'),
					};
				}

				return {
					valid: true,
				};
			});
		}

		return {
			handler: panel,
			dom: panel.panel,
		};
	}

	// TODO move logic to the connection manager
	_extendHandler(instructions) {
		return (event) => {
			if (this.panel) return;

			if (!event.detail?.element) return;

			const option = event.detail.element;

			if (option.getAttribute('data-scoped') === 'true') return;

			let connectionId = parseInt(option.value);
			let isPersonalConnection = false;
			let scenarioVariable = null;

			// Skip placeholders
			if (Number.isNaN(connectionId) && !option.value.startsWith('PAC_')) {
				const pattern = /{{var\.input\.(\w+)}}/;
				const match = option?.value?.match(pattern);

				if (match && match[1]) {
					scenarioVariable = match[1];
					connectionId = this.form.meta.imlOptions?.buildTimeVariables?.[scenarioVariable];

					if (connectionId?.startsWith) {
						if (connectionId.startsWith('PAC_')) {
							isPersonalConnection = true;
						} else if (connectionId.startsWith('TAC_')) {
							connectionId = +connectionId.replace('TAC_', '');
						}
					}
				} else {
					return;
				}
			}

			if (option?.value?.startsWith && option.value.startsWith('PAC_')) {
				// Personal connection - PAC_...
				connectionId = option.value;
				isPersonalConnection = true;
			}

			this.panel = create('imt-panel');
			this.panel.width = 350;
			this.panel.position = ['right', 'left'];
			this.panel.relative = event.target;

			const title = create('h1');

			title.textContent = I18n.l('panels.account.extend');
			this.panel.header.append(title);
			this.panel.header.addCloseButton();
			this.panel.theme = instructions?.theme;

			const content = create('p');

			content.textContent = I18n.l('panels.account.extendhelp');
			this.panel.body.append(content);

			EventManager.dispatch(this, EVENTS.ACCOUNT.EXTEND_PANEL_CREATED, { bubbles: true, detail: this.panel });

			this.panel.addEventListener(EVENTS.PANEL.CLOSE, (event) => {
				this.disabled = false;
				this.panel = null;

				event.preventDefault();
			});

			const scope =
				instructions.connectionTypes.filter(
					(connection) => connection.type === option.getAttribute('data-connection'),
				)[0]?.scope || [];

			const continueButton = create('button.btn.btn-sm.btn-primary.ml-auto');

			this.panel._extendScopeData = {
				scope,
				connectionId,
				isPersonalConnection,
				option,
				scenarioVariable,
			};

			continueButton.textContent = I18n.l('common.continue');

			EventManager.addNamedNativeListener(continueButton, EVENTS.ACCOUNT.EXTEND_CLICK, 'click', async (event) => {
				this.panel.setLoading(I18n.l('panels.account.extending'));

				try {
					await OAuth.extend.call(this.panel, connectionId, scope, isPersonalConnection);

					this.panel.close();
					this.panel = null;

					option.setAttribute('data-scoped', 'true');
					this.value = connectionId;

					if (scenarioVariable) {
						EventManager.dispatch(this, EVENTS.ACCOUNT.SCENARIO_INPUT_EXTENDED, {
							detail: {
								name: scenarioVariable,
								scope,
							},
							cancelable: true,
						});
					}
				} catch (ex) {
					this.panel.warn(ex);
				}
			});

			this.panel.footer.append(continueButton);
			this.panel.open();

			event.preventDefault();
		};
	}

	/** Allows clicking on the icon to open the picker / create connection panel. */
	setIconClickHandler() {
		const icon = this.querySelector(':scope > .input-group > .input-icon');

		if (!icon) return;

		icon.onclick = () => {
			const mainElement = this.querySelector(
				':scope > .input-group > .create-connection, :scope > .input-group > imt-picker',
			);

			mainElement?.click();
			mainElement?.focus();
		};
	}

	setThemeVariables(appData) {
		try {
			const color = typeof appData.theme === 'string' ? appData.theme : appData.theme.toHex();

			this.style.setProperty('--acc-theme-color', color);
			this.style.setProperty('--acc-theme-hover-color', getAdjustedColor(color, 0.16));
			this.style.setProperty('--acc-theme-active-color', getAdjustedColor(color, 0.32));

			if (!isVisibleOnWhite(color)) {
				this.classList.add('low-contrast');
			}
		} catch (e) {
			console.error(e);
		}
	}
}

registerCustomElement('imt-input-account', AccountInput);
